import React, { useMemo } from 'react'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import { Layout, Sidebar, TwoColumns } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import CityDropdown from 'app/theme/CityDropdown'
import MapListToggle from 'app/theme/MapListToggle'
import PillToggle from 'app/theme/PillToggle'
import EventMap from 'app/theme/EventMap'
import BookTruckBanner from 'app/theme/banners/BookTruckBar'
import ShadowBox from 'app/caterers/ShadowBox'
import Pagination from 'app/theme/Pagination'
import HtmlContent from 'app/theme/HtmlContent'
import NotFoundPage from 'app/pages/NotFoundPage'
import { format } from 'date-fns'
import { useSearchParams } from 'app/theme/useSearchParams'

const monthYear = format(new Date(), 'MMMM yyyy')

export default function CityPage({ city, page, tagSlug }) {
  const path = `/food-trucks/${city.url_slug}`
  const crumbs = getCrumbs(city)
  const isMapPage = !page && !tagSlug
  const subcities = city.cities || city.neighborhoods

  const [searchParams] = useSearchParams()

  const areaFilter = {}

  if (city.type === 'city' || !city.location) {
    areaFilter.city_id = city.id
  } else {
    areaFilter.service_area = {
      contains: JSON.stringify({ ...city.location, radius: searchParams.service_radius || 10 })
    }
  }

  const query = useQuery(['caterers', {
    sort: '-ranking',
    filter: {
      ...areaFilter,
      tag_slug: tagSlug || undefined,
      closed: false,
    },
    include: [
      'photo',
    ],
    fields: {
      caterers: ['name', 'slug'],
      photo: ['path']
    },
    page: {
      number: page || 1,
      size: 21
    },
    stats: {
      total: 'count',
    }
  }])

  const tagQuery = useQuery(tagSlug && ['tags', {
    filter: {
      slug: tagSlug,
    },
    fields: {
      tags: ['name', 'slug']
    }
  }])

  let caterers = query.data ? [...query.data] : []
  let tag = tagQuery.data ? tagQuery.data[0] : null

  const googleMetaTags = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'itemListElement': caterers.map((caterer, index) => ({
        '@type': 'ListItem',
        position: index,
        url: `https://roaminghunger.com/${caterer.slug}/`
      }))
    })
  }, [caterers])

  if (query.isLoading || tagQuery.isLoading) {
    return null
  }

  const pageOutOfRange = page && Math.ceil(query.meta?.stats?.total?.count / 21) < page

  if ((tagSlug && !tag) || pageOutOfRange) {
    return <NotFoundPage />
  }

  if (caterers.length && page || tagSlug) {
    caterers.splice(3, 0, 'banner')
  }

  let bannerUrl
  if (tag) {
    bannerUrl = `/catering/${city.url_slug}/${tag.slug}/`
  } else {
    bannerUrl = `/${city.slug}/food-truck-catering/`
  }

  const cityDisplayName = [
    city.name,
    city.country?.code === 'US' ? city.region?.code : (city.region?.name || city.country?.name)
  ].filter(Boolean).join(', ')

  let metaTitle
  let metaDescription

  if (isMapPage) {
    metaTitle = `Food Truck Map - ${cityDisplayName} - Catering & Events - Roaming Hunger`
    metaDescription = `Locate the best Food Trucks near you in ${cityDisplayName} and find the perfect food truck to cater your office, party, wedding or next event.`
  } else if (tag) {
    metaTitle = `${cityDisplayName} ${tag.name} Food Trucks (${monthYear}) - Roaming Hunger`
    metaDescription = `Find and Book the Best ${tag.name} Food Trucks in ${cityDisplayName} for your next catering or event.`
  } else {
    metaTitle = `The 21 Best ${cityDisplayName} Food Trucks (${monthYear}) - Roaming Hunger`
    metaDescription = `The 21 Best Food Trucks in ${cityDisplayName} for corporate catering, events, parties, and street service.`
  }

  const metaKeywords = [
    city.name,
    city.region?.name,
    city.country && city.country.code !== 'US' && city.country.name,
    tag && tag.name,
    'catering,events,food trucks,food carts,food trucks near me,street food,truck schedules'
  ].filter(Boolean).join(',')

  return (
    <Layout className="container py-10">
      <TwoColumns sidebar={<Sidebar city={city} tag={tag} />}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription}></meta>
          <meta name="keywords" content={metaKeywords}></meta>
          <meta name="og:title" content={metaTitle}></meta>
          <script type="application/ld+json">
            {googleMetaTags}
          </script>
        </Helmet>
        <Breadcrumbs
          crumbs={crumbs}
        />
        <div className="mt-4 flex items-center">
          <h1 className="text-3xl lg:text-4xl text-navy font-platform leading-tight">
            {city.name} Food Trucks
          </h1>
          {/*
          {subcities?.length > 0 && (
            <CityDropdown
              cities={subcities}
              className="ml-3 mt-2"
            />
          )}
          */}
        </div>
        {!isMapPage && city.short_description && (
          <HtmlContent
            className="mt-3"
            content={city.short_description}
          />
        )}
        {isMapPage ? (
          <div>
            <div className="sm:flex justify-between items-center pt-4 mt-4 mb-1 border-t border-gray-300">
              <div className="text-navy text-2xl font-platform">
                Live Map
              </div>
              <MapListToggle
                active="map"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
              />
            </div>
            <EventMap
              lat={city.location.lat}
              lng={city.location.lng}
              center={`${city.name}, ${city.region ? `${city.region.name}, ` : ''}${city.country.name}`}
              boundingBox={city.bounding_box}
              timezone={city.timezone}
              zoom={city.type === 'neighborhood' ? 10 : 8}
            />
            <BookTruckBanner
              className="mt-6"
              url={bannerUrl}
            />
            <div className="mt-8 mb-5 text-navy text-4xl font-platform">
              Food Trucks
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {caterers.map(caterer => (
                <ShadowBox
                  key={caterer.id}
                  caterer={caterer}
                  photo={caterer.photo}
                />
              ))}
            </div>
            {caterers.length > 20 && (
              <div className="flex justify-center mt-10">
                <a
                  href={`${path}/1/`}
                  className="btn btn-blue px-10 py-2 text-lg"
                  children="More Trucks"
                />
              </div>
            )}
            {city.description && (
              <HtmlContent
                className="mt-10 pt-10 border-t border-gray-300"
                content={city.description}
              />
            )}
          </div>
        ) : (
          <div>
            <div
              className={[
                'justify-between items-center mt-4 pt-4 pb-3',
                'border-t border-gray-300',
                'sm:flex sm:pb-1 md:mb-2'
              ]}
            >
              <MapListToggle
                active="list"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
                className="sm:order-2"
              />
              <PillToggle
                pills={[
                  { text: 'Preferred', url: `${path}/1/`, active: !tagSlug },
                  { text: 'Savory', url: `${path}/savory/`, active: tagSlug === 'savory' },
                  { text: 'Sweet', url: `${path}/sweet/`, active: tagSlug === 'sweet' },
                  { text: 'Vegetarian', url: `${path}/vegetarian/`, active: tagSlug === 'vegetarian' },
                ]}
              />
            </div>
            {tag && (
              <div className="text-navy leading-none text-xl mb-6 md:text-2xl">
                {tag.name} Food Trucks and {tag.name} Food Carts in {city.name}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-6">
              {caterers.map((caterer, index) => (
                caterer === 'banner' ? (
                  <div key="banner" className="hidden md:flex md:col-span-3">
                    <BookTruckBanner url={bannerUrl} />
                  </div>
                ) : (
                  <ShadowBox
                    key={caterer.id}
                    caterer={caterer}
                    photo={caterer.photo}
                  />
                )
              ))}
            </div>
            {query.meta && (
              <Pagination
                className="justify-center mt-10"
                total={query.meta.stats.total.count}
                current={page || 1}
                limit={21}
                getLinkProps={link => ({
                  href: tag
                    ? `${path}/${tag.slug}/${link.page === 1 ? '' : `${link.page}/`}`
                    : `${path}/${link.page}/`
                })}
                extraLinks={({ hasPreviousPage, hasNextPage }) => (
                  <Helmet>
                    {hasPreviousPage && (
                      <link
                        rel="prev"
                        href={ tag
                          ? `${path}/${tag.slug}/${page - 1 === 1 ? '' : `${page}/`}`
                          : `${path}/${(page || 1) - 1}/`
                        }
                      />
                    )}
                    {hasNextPage && (
                      <link rel="next" href={tag ? `${path}/${tag.slug}/${parseInt(page || 1) + 1}` : `${path}/${parseInt(page || 1) + 1}/`} />
                    )}
                  </Helmet>
                )}
              />
            )}
          </div>
        )}
      </TwoColumns>
    </Layout>
  )
}

const getCrumbs = city => {
  const crumbs = [
    { text: 'Food Trucks', url: `/food-trucks/` },
  ]

  if (city.region) {
    crumbs.push({
      text: city.region.name,
      url: `/food-trucks/${city.region.url_slug}/`
    })
  }

  if (city.county) {
    crumbs.push({
      text: city.county.name,
      url: `/food-trucks/${city.county.url_slug}/`
    })
  }

  if (city.city) {
    crumbs.push({
      text: city.city.name,
      url: `/food-trucks/${city.city.url_slug}/`
    })
  }


  return crumbs.concat({
    text: city.name,
  })
}
